import { gql, type TypedDocumentNode } from "@apollo/client";
import type {
  CreatePackageMutation,
  CreatePackageMutationVariables,
  UpdatePackageMutation,
  UpdatePackageMutationVariables,
  PublishPackageMutation,
  PublishPackageMutationVariables,
  ArchivePackageMutation,
  ArchivePackageMutationVariables,
  PackageUploadMutation,
  PackageUploadMutationVariables,
  CancelPackageCheckoutMutation,
  CancelPackageCheckoutMutationVariables,
  PackageCheckoutMutation,
  PackageCheckoutMutationVariables,
  PackageFulfilmentMutation,
  PackageFulfilmentMutationVariables,
  PackageStripeIntentMutation,
  PackageStripeIntentMutationVariables,
  VerifyPackagePaymentMutation,
  VerifyPackagePaymentMutationVariables,
} from "@src/@types/api.d";
import { PackageDetails } from "../fragments/packages.fragments";

export const CreatePackage: TypedDocumentNode<
  CreatePackageMutation,
  CreatePackageMutationVariables
> = gql`
  mutation CreatePackage($eventId: UUID!, $input: CreateAseobiPackageInput!) {
    createAseobiPackage(event_id: $eventId, input: $input) {
      ...PackageDetails
    }
  }
  ${PackageDetails}
`;

export const UpdatePackage: TypedDocumentNode<
  UpdatePackageMutation,
  UpdatePackageMutationVariables
> = gql`
  mutation UpdatePackage(
    $eventId: UUID!
    $packageId: UUID!
    $version: Int!
    $input: UpdateAseobiPackageInput!
  ) {
    updateAseobiPackage(
      event_id: $eventId
      package_id: $packageId
      expected_version: $version
      input: $input
    ) {
      ...PackageDetails
    }
  }
  ${PackageDetails}
`;

export const PublishPackage: TypedDocumentNode<
  PublishPackageMutation,
  PublishPackageMutationVariables
> = gql`
  mutation PublishPackage($eventId: UUID!, $packageId: UUID!, $version: Int!) {
    publishAseobiPackage(
      event_id: $eventId
      package_id: $packageId
      expected_version: $version
    ) {
      ...PackageDetails
    }
  }
  ${PackageDetails}
`;

export const ArchivePackage: TypedDocumentNode<
  ArchivePackageMutation,
  ArchivePackageMutationVariables
> = gql`
  mutation ArchivePackage($eventId: UUID!, $packageId: UUID!, $version: Int!) {
    archiveAseobiPackage(
      event_id: $eventId
      package_id: $packageId
      expected_version: $version
    ) {
      ...PackageDetails
    }
  }
  ${PackageDetails}
`;

export const PackageUpload: TypedDocumentNode<
  PackageUploadMutation,
  PackageUploadMutationVariables
> = gql`
  mutation PackageUpload(
    $eventId: UUID!
    $packageId: UUID!
    $itemId: UUID
    $mediaType: String!
  ) {
    generateAseobiPackageMediaUploadUrl(
      event_id: $eventId
      package_id: $packageId
      item_id: $itemId
      mediaType: $mediaType
    ) {
      upload_url
      file_uri
    }
  }
`;

export const CancelPackageCheckout: TypedDocumentNode<
  CancelPackageCheckoutMutation,
  CancelPackageCheckoutMutationVariables
> = gql`
  mutation CancelPackageCheckout($eventId: UUID!, $reference: String!) {
    cancelAsoebiCheckout(event_id: $eventId, payment_reference: $reference)
  }
`;

export const PackageCheckout: TypedDocumentNode<
  PackageCheckoutMutation,
  PackageCheckoutMutationVariables
> = gql`
  mutation PackageCheckout(
    $eventId: UUID!
    $input: AseobiPaymentMetadataRequest!
  ) {
    generateAsoebiPaymentMetadata(event_id: $eventId, input: $input) {
      payment_reference
      payment_provider
      contact_email
      currency
      total_payment_intent_amount
      total_amount
      checkout_id
      expires_at
      delivery_fee
      vat
      late_fee
      transaction_charge
      split_payments {
        sub_account_code
        split_percentage
      }
      aseobi_asset {
        sub_account_id
      }
      packages {
        line_id
        package_id
        package_version
        name
        quantity
        currency
        unit_price
        unit_fee
        subtotal
        fee_amount
        total
        components {
          id
          name
          description
          images
          quantity
          unit
        }
      }
      attires {
        attire_id
        quantity
        size
        size_unit
        total_price
      }
    }
  }
`;

export const PackageFulfilment: TypedDocumentNode<
  PackageFulfilmentMutation,
  PackageFulfilmentMutationVariables
> = gql`
  mutation PackageFulfilment(
    $eventId: UUID!
    $orderId: UUID!
    $status: AseobiOrderStatusEnum!
  ) {
    updateAseobiOrderStatus(
      event_id: $eventId
      order_id: $orderId
      status: $status
    )
  }
`;

export const PackageStripeIntent: TypedDocumentNode<
  PackageStripeIntentMutation,
  PackageStripeIntentMutationVariables
> = gql`
  mutation PackageStripeIntent($args: initiatePaymentInput!) {
    initiateStripePaymentIntent(args: $args) {
      client_secret
      reference
    }
  }
`;

export const VerifyPackagePayment: TypedDocumentNode<
  VerifyPackagePaymentMutation,
  VerifyPackagePaymentMutationVariables
> = gql`
  mutation VerifyPackagePayment(
    $reference: String!
    $provider: paymentProcessor!
    $subAccountId: String
  ) {
    verifyTransaction(
      reference: $reference
      provider: $provider
      sub_account_id: $subAccountId
    ) {
      reference
    }
  }
`;
